// ============================================================================
// MEAL PLAN HOOKS
// ============================================================================
// React hooks for Meal Plan API operations

import { useState, useEffect, useCallback } from 'react';
import { mealPlanService } from 'api/services/mealPlanService';
import type {
  MealPlan,
  CreateMealPlanRequest,
  UpdateMealPlanRequest,
  MealPlanSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all meal plans (paginated)
 */
export const useMealPlans = (params?: MealPlanSearchRequest) => {
  const [data, setData] = useState<MealPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchMealPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await mealPlanService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchMealPlans();
  }, [fetchMealPlans]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchMealPlans,
  };
};

/**
 * Hook to fetch a single meal plan by ID
 */
export const useMealPlan = (id: ID) => {
  const [data, setData] = useState<MealPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMealPlan = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await mealPlanService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMealPlan();
  }, [fetchMealPlan]);

  return {
    data,
    loading,
    error,
    refetch: fetchMealPlan,
  };
};

/**
 * Hook to create a new meal plan
 */
export const useCreateMealPlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createMealPlan = async (
    data: CreateMealPlanRequest,
    options?: {
      onSuccess?: (mealPlan: MealPlan) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await mealPlanService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createMealPlan,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a meal plan
 */
export const useUpdateMealPlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateMealPlan = async (
    id: ID,
    data: UpdateMealPlanRequest,
    options?: {
      onSuccess?: (mealPlan: MealPlan) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await mealPlanService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateMealPlan,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a meal plan
 */
export const useDeleteMealPlan = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteMealPlan = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await mealPlanService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteMealPlan,
    loading,
    error,
    success,
  };
};
